import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  LinearProgress,
  Tooltip,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  UploadFile,
  Add,
  Delete,
  CheckCircle,
  ErrorOutline,
  PersonAdd,
  MarkEmailRead,
  Warning,
} from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { selectableTerms } from '../../lib/term-options';
import { useCanManageAcademics } from '../../hooks/use-can-manage-academics';
import { callable } from '../../lib/callable';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StudentRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organizationName: string;
}

type BulkEnrollStatus = 'created' | 'exists' | 'enrolled_email_failed' | 'error';

interface EnrollResult {
  email: string;
  status: BulkEnrollStatus;
  personId?: string;
  emailSent?: boolean;
  message?: string;
}

interface Program {
  id: string;
  name: string;
}
interface Term {
  id: string;
  name: string;
  status: string;
}

const EMPTY_ROW = (): StudentRow => ({
  id: crypto.randomUUID(),
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  organizationName: '',
});

const STEPS = ['Destino', 'Alumnos', 'Resultado'];

// ─── Status chip ─────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: BulkEnrollStatus }) {
  if (status === 'created')
    return <Chip label="✅ Creado" color="success" size="small" variant="outlined" />;
  if (status === 'exists')
    return <Chip label="⚠️ Ya existía" color="warning" size="small" variant="outlined" />;
  if (status === 'enrolled_email_failed')
    return (
      <Chip
        label="📧 Sin correo"
        color="warning"
        size="small"
        sx={{ borderColor: 'orange', color: 'darkorange' }}
        variant="outlined"
      />
    );
  return <Chip label="❌ Error" color="error" size="small" variant="outlined" />;
}

function StatusDetail({ r }: { r: EnrollResult }) {
  if (r.status === 'created') return <>Alumno creado y correo enviado ✉️</>;
  if (r.status === 'exists') return <>Ya tenía cuenta — correo reenviado</>;
  if (r.status === 'enrolled_email_failed')
    return (
      <>
        <Warning fontSize="inherit" sx={{ color: 'darkorange', mr: 0.5 }} />
        Inscrito — correo no enviado: {r.message ?? 'error de email'}
      </>
    );
  return <>{r.message ?? 'Error desconocido'}</>;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BulkEnrollPage() {
  // MATRICULAR EN MASA ES TRABAJO DE REGISTRO. Esta pantalla cuelga de
  // `/sections`, al que también llegan el docente y el mentor para ver sus
  // cursos; el servidor ya lo rechaza, y dejarles cargar el archivo entero
  // para fallar al final sería peor que decirlo de entrada.
  const puedeGestionar = useCanManageAcademics();
  // El espacio 'sis' porque la clave vive ahí; el de por defecto es 'common'
  // y habría mostrado la clave cruda en pantalla.
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();

  // Step control
  const [step, setStep] = useState(0);

  // Step 1 — program + term
  const [programs, setPrograms] = useState<Program[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [programId, setProgramId] = useState('');
  const [termId, setTermId] = useState('');

  // Step 2 — student rows
  const [rows, setRows] = useState<StudentRow[]>([EMPTY_ROW()]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Step 3 — processing
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<EnrollResult[]>([]);
  const [done, setDone] = useState(false);

  // Retry-email state: maps email → loading
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});

  // Ver `lib/term-options.ts`: se excluye SOLO el período cerrado.
  const termOptions = selectableTerms(terms);

  // ── Load programs & terms ─────────────────────────────────────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub1 = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) =>
      setPrograms(
        snap.docs
          .map((d) => ({
            id: d.id,
            name: d.data()['name'] ?? d.id,
            status: d.data()['status'] ?? 'active',
          }))
          .filter((p) => p.status === 'active'),
      ),
    );
    const unsub2 = onSnapshot(collection(db, `tenants/${currentTenantId}/terms`), (snap) =>
      setTerms(
        snap.docs.map((d) => ({
          id: d.id,
          name: d.data()['name'] ?? d.id,
          status: d.data()['status'] ?? '',
        })),
      ),
    );
    return () => {
      unsub1();
      unsub2();
    };
  }, [currentTenantId]);

  // ── Row editing ───────────────────────────────────────────────────────────
  const updateRow = useCallback((id: string, field: keyof StudentRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  }, []);

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));

  // ── Excel import ──────────────────────────────────────────────────────────
  const [importError, setImportError] = useState<string | null>(null);

  // Normalize a string key: lowercase, strip accents, remove spaces/dots
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s._-]+/g, '');

  const pick = (row: Record<string, any>, ...keys: string[]): string => {
    const rowNorm = Object.fromEntries(Object.entries(row).map(([k, v]) => [normalize(k), v]));
    for (const k of keys) {
      const val = rowNorm[normalize(k)];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val).trim();
      }
    }
    return '';
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]!]!;

        // ── First attempt: read with headers ─────────────────────────────
        const json: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (json.length === 0) {
          setImportError('El archivo está vacío o no tiene filas de datos.');
          return;
        }

        const tryMapWithHeaders = (rows: any[]): StudentRow[] =>
          rows
            .map((r) => ({
              id: crypto.randomUUID(),
              firstName: pick(r, 'Nombre', 'nombre', 'firstName', 'first_name', 'nombres', 'name'),
              lastName: pick(
                r,
                'Apellido',
                'apellido',
                'lastName',
                'last_name',
                'apellidos',
                'surname',
              ),
              email: pick(
                r,
                'Email',
                'email',
                'correo',
                'correo electronico',
                'e-mail',
                'mail',
              ).toLowerCase(),
              phone: pick(r, 'Telefono', 'teléfono', 'phone', 'celular', 'tel', 'movil', 'móvil'),
              organizationName: pick(
                r,
                'Organizacion',
                'organización',
                'organizationName',
                'iglesia',
                'church',
                'institution',
                'institución',
              ),
            }))
            .filter((r) => r.email || r.firstName);

        const imported = tryMapWithHeaders(json);

        if (imported.length > 0) {
          setRows(imported);
          return;
        }

        // ── Fallback: no recognized headers → try positional mapping ─────
        // Read raw rows as arrays (no header parsing)
        const rawRows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        // Skip any leading empty rows
        const dataRows = rawRows.filter((row) => row.some((cell) => String(cell).trim() !== ''));

        if (dataRows.length === 0) {
          setImportError('El archivo está vacío o no tiene datos en las celdas.');
          return;
        }

        // Check if first row looks like headers (none of the cells look like an email)
        const firstRow = dataRows[0] ?? [];
        const firstRowHasEmail = firstRow.some((cell) => String(cell).includes('@'));

        // If first row has an email it's likely data (no headers) → use all rows
        // Otherwise skip first row assuming it's a header we couldn't match
        const startIndex = firstRowHasEmail ? 0 : 1;
        const positionalRows: StudentRow[] = dataRows
          .slice(startIndex)
          .map((row) => ({
            id: crypto.randomUUID(),
            firstName: String(row[0] ?? '').trim(),
            lastName: String(row[1] ?? '').trim(),
            email: String(row[2] ?? '')
              .trim()
              .toLowerCase(),
            phone: String(row[3] ?? '').trim(),
            organizationName: String(row[4] ?? '').trim(),
          }))
          .filter((r) => r.email || r.firstName);

        if (positionalRows.length === 0) {
          const cols = Object.keys(json[0] ?? {}).join(', ');
          setImportError(
            `No se reconocieron columnas válidas. Columnas detectadas: "${cols}". ` +
              `Asegúrate de que el archivo tenga encabezados (Nombre, Apellido, Email) ` +
              `o que los datos estén en las primeras columnas (A=Nombre, B=Apellido, C=Email).`,
          );
          return;
        }

        // Successfully imported via positional mapping — warn the user about the assumption
        setImportError(
          `⚠️ El archivo no tiene encabezados reconocibles. Se importaron ${positionalRows.length} fila(s) ` +
            `asumiendo el orden: columna A = Nombre, B = Apellido, C = Email, D = Teléfono, E = Organización. ` +
            `Revisa que los datos estén correctos antes de continuar.`,
        );
        setRows(positionalRows);
      } catch (err: any) {
        setImportError(`Error al leer el archivo: ${err?.message ?? 'formato no reconocido'}`);
      }
    };
    reader.onerror = () => setImportError('No se pudo leer el archivo.');
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validRows = rows.filter((r) => r.firstName.trim() && r.email.trim());
  const canProceed1 = !!programId;
  const canProceed2 = validRows.length > 0;

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!currentTenantId) return;
    setProcessing(true);
    setProgress(0);
    setResults([]);
    setStep(2);

    const fn = callable<unknown, { results: EnrollResult[] }>('bulkEnrollStudents');

    // Process in small batches of 5 to show progress
    const batchSize = 5;
    const allResults: EnrollResult[] = [];
    let processed = 0;

    for (let i = 0; i < validRows.length; i += batchSize) {
      const batch = validRows.slice(i, i + batchSize);
      try {
        const res = await fn({
          tenantId: currentTenantId,
          programId,
          ...(termId ? { termId } : {}),
          students: batch.map((r) => ({
            firstName: r.firstName.trim(),
            lastName: r.lastName.trim(),
            email: r.email.trim().toLowerCase(),
            ...(r.phone ? { phone: r.phone.trim() } : {}),
            ...(r.organizationName ? { organizationName: r.organizationName.trim() } : {}),
          })),
        });
        allResults.push(...res.data.results);
      } catch (err: any) {
        batch.forEach((s) =>
          allResults.push({
            email: s.email,
            status: 'error',
            emailSent: false,
            message: err?.message ?? 'Error',
          }),
        );
      }
      processed += batch.length;
      setProgress(Math.round((processed / validRows.length) * 100));
      setResults([...allResults]);
    }

    setDone(true);
    setProcessing(false);
  };

  // ── Retry single email ────────────────────────────────────────────────────
  const handleRetryEmail = async (r: EnrollResult) => {
    if (!currentTenantId || !r.personId) return;
    setRetrying((prev) => ({ ...prev, [r.email]: true }));
    try {
      const fn = callable('resendBulkWelcomeEmail');
      await fn({ tenantId: currentTenantId, email: r.email, personId: r.personId, programId });
      // Update local results
      setResults((prev) =>
        prev.map((res) =>
          res.email === r.email
            ? { ...res, status: 'exists' as BulkEnrollStatus, emailSent: true }
            : res,
        ),
      );
    } catch (err: any) {
      setResults((prev) =>
        prev.map((res) =>
          res.email === r.email ? { ...res, message: err?.message ?? 'Error al reenviar' } : res,
        ),
      );
    } finally {
      setRetrying((prev) => {
        const n = { ...prev };
        delete n[r.email];
        return n;
      });
    }
  };

  // ── Reset for a new import (pre-populating only the failed rows) ──────────
  const handleRetryFailed = () => {
    const failedRows: StudentRow[] = results
      .filter((r) => r.status === 'error')
      .map((r) => ({
        id: crypto.randomUUID(),
        firstName: '',
        lastName: '',
        email: r.email,
        phone: '',
        organizationName: '',
      }));
    setRows(failedRows.length > 0 ? failedRows : [EMPTY_ROW()]);
    setResults([]);
    setDone(false);
    setProgress(0);
    setStep(1);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (!puedeGestionar) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('bulkEnroll.staffOnly')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, px: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={() => navigate('/sections')} size="small">
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Inscripción Directa de Alumnos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registra alumnos sin proceso de postulación y envíales el correo de bienvenida.
          </Typography>
        </Box>
      </Stack>

      {/* Stepper */}
      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ── STEP 1: Destino ─────────────────────────────────────────────── */}
      {step === 0 && (
        <Stack spacing={3}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Selecciona el <strong>programa académico</strong> al que serán inscritos estos alumnos.
            El período es opcional; si no lo indicas, el alumno verá todos los períodos abiertos al
            iniciar sesión.
          </Alert>

          <FormControl fullWidth>
            <InputLabel>Programa Académico *</InputLabel>
            <Select
              value={programId}
              label="Programa Académico *"
              onChange={(e) => setProgramId(e.target.value)}
            >
              {programs.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('sis:bulkEnroll.term_label')}</InputLabel>
            <Select
              value={termId}
              label={t('sis:bulkEnroll.term_label')}
              onChange={(e) => setTermId(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('sis:bulkEnroll.term_none')}</em>
              </MenuItem>
              {termOptions.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
            {termOptions.length === 0 && (
              <FormHelperText>{t('sis:bulkEnroll.term_none_selectable')}</FormHelperText>
            )}
          </FormControl>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={!canProceed1}
              endIcon={<ArrowForward />}
              onClick={() => setStep(1)}
              sx={{ px: 4 }}
            >
              Continuar
            </Button>
          </Box>
        </Stack>
      )}

      {/* ── STEP 2: Alumnos ─────────────────────────────────────────────── */}
      {step === 1 && (
        <Stack spacing={2}>
          {/* Actions bar */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" color="text.secondary">
              {validRows.length} alumno{validRows.length !== 1 ? 's' : ''} válido
              {validRows.length !== 1 ? 's' : ''} (nombre + email requeridos)
            </Typography>
            <Stack direction="row" spacing={1}>
              <input
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                ref={fileRef}
                onChange={handleFileUpload}
              />
              <Tooltip title="Sube un Excel con columnas: Nombre, Apellido, Email, Telefono, Organizacion">
                <label htmlFor="excel-file-input" style={{ cursor: 'pointer' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFile />}
                    component="span"
                  >
                    Subir Excel
                  </Button>
                </label>
              </Tooltip>
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={addRow}>
                Agregar fila
              </Button>
            </Stack>
          </Stack>

          {/* Import error feedback */}
          {importError && (
            <Alert severity="warning" onClose={() => setImportError(null)} sx={{ borderRadius: 2 }}>
              {importError}
            </Alert>
          )}

          {/* Editable table */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover', fontSize: 12 } }}
                >
                  <TableCell width={36} align="center" sx={{ color: 'text.disabled' }}>
                    #
                  </TableCell>
                  <TableCell>Nombre *</TableCell>
                  <TableCell>Apellido</TableCell>
                  <TableCell>Email *</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Organización</TableCell>
                  <TableCell width={40} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      borderLeft:
                        !row.firstName.trim() || !row.email.trim()
                          ? '3px solid'
                          : '3px solid transparent',
                      borderLeftColor:
                        !row.firstName.trim() || !row.email.trim() ? 'warning.main' : 'transparent',
                      bgcolor:
                        !row.firstName.trim() || !row.email.trim()
                          ? 'rgba(251,191,36,0.05)'
                          : undefined,
                      opacity: !row.firstName.trim() || !row.email.trim() ? 0.75 : 1,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        color: 'text.disabled',
                        fontSize: 11,
                        p: '4px 6px',
                        userSelect: 'none',
                      }}
                    >
                      {idx + 1}
                    </TableCell>
                    {(['firstName', 'lastName', 'email', 'phone', 'organizationName'] as const).map(
                      (field) => (
                        <TableCell key={field} sx={{ p: '4px 8px' }}>
                          <TextField
                            size="small"
                            variant="standard"
                            fullWidth
                            value={row[field]}
                            onChange={(e) => updateRow(row.id, field, e.target.value)}
                            InputProps={{ disableUnderline: false }}
                            placeholder={field === 'email' ? 'correo@ejemplo.com' : ''}
                            type={field === 'email' ? 'email' : 'text'}
                          />
                        </TableCell>
                      ),
                    )}
                    <TableCell align="center" sx={{ p: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Button variant="text" startIcon={<ArrowBack />} onClick={() => setStep(0)}>
              Atrás
            </Button>
            <Button
              variant="contained"
              disabled={!canProceed2}
              startIcon={<PersonAdd />}
              onClick={handleSubmit}
              sx={{ px: 4 }}
            >
              Inscribir {validRows.length} alumno{validRows.length !== 1 ? 's' : ''}
            </Button>
          </Stack>
        </Stack>
      )}

      {/* ── STEP 3: Resultado ───────────────────────────────────────────── */}
      {step === 2 && (
        <Stack spacing={3}>
          {/* Progress bar */}
          {processing && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Procesando alumnos… {progress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ borderRadius: 2, height: 8 }}
              />
            </Box>
          )}

          {/* Live results table */}
          {results.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover', fontSize: 12 } }}
                  >
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell>Detalle</TableCell>
                    <TableCell align="center" width={120} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {r.email}
                      </TableCell>
                      <TableCell align="center">
                        <StatusChip status={r.status} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        <StatusDetail r={r} />
                      </TableCell>
                      <TableCell align="center">
                        {r.status === 'enrolled_email_failed' && r.personId && (
                          <Tooltip title="Reenviar correo de bienvenida">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={
                                  retrying[r.email] ? (
                                    <CircularProgress size={12} />
                                  ) : (
                                    <MarkEmailRead fontSize="small" />
                                  )
                                }
                                disabled={!!retrying[r.email]}
                                onClick={() => handleRetryEmail(r)}
                                sx={{ fontSize: 11, py: 0.5 }}
                              >
                                Reenviar
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Summary when done */}
          {done && (
            <Stack spacing={2}>
              {/* Email-failed banner */}
              {results.some((r) => r.status === 'enrolled_email_failed') && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  <strong>Alumnos inscritos sin correo:</strong> los registros fueron creados
                  correctamente. Usa el botón <em>Reenviar</em> en cada fila para volver a enviar el
                  correo de bienvenida, o verifica la configuración de Resend (el correo destino
                  debe estar verificado en el plan gratuito).
                </Alert>
              )}

              <Stack direction="row" spacing={2}>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 2,
                    bgcolor: 'success.lighter',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.light',
                  }}
                >
                  <CheckCircle color="success" />
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {results.filter((r) => r.status === 'created').length}
                  </Typography>
                  <Typography variant="body2">Creados y notificados</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 2,
                    bgcolor: 'warning.lighter',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'warning.light',
                  }}
                >
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {results.filter((r) => r.status === 'exists').length}
                  </Typography>
                  <Typography variant="body2">Ya existían (correo reenviado)</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 2,
                    bgcolor: 'warning.lighter',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'orange',
                  }}
                >
                  <Warning sx={{ color: 'darkorange' }} />
                  <Typography variant="h4" fontWeight={700} sx={{ color: 'darkorange' }}>
                    {results.filter((r) => r.status === 'enrolled_email_failed').length}
                  </Typography>
                  <Typography variant="body2">Inscritos sin correo</Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    p: 2,
                    bgcolor: 'error.lighter',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'error.light',
                  }}
                >
                  <ErrorOutline color="error" />
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {results.filter((r) => r.status === 'error').length}
                  </Typography>
                  <Typography variant="body2">Con errores</Typography>
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                {results.some((r) => r.status === 'error') && (
                  <Tooltip title="Vuelve al paso 2 con solo los alumnos que fallaron, para corregir y reintentar">
                    <Button variant="outlined" color="error" onClick={handleRetryFailed}>
                      Reintentar fallidos
                    </Button>
                  </Tooltip>
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setStep(0);
                    setRows([EMPTY_ROW()]);
                    setProgramId('');
                    setTermId('');
                    setResults([]);
                    setDone(false);
                  }}
                >
                  Nueva importación
                </Button>
                <Button variant="contained" onClick={() => navigate('/people')}>
                  Ver Personas
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}
